import {
  CUSTOM_FILTER_ENTITY,
  conditionToCustomFilter,
  customFilterToCondition,
  getCollectionFields,
} from './customFilters'

export function buildEntityListParams(siteId, extraParams = {}) {
  return {
    report: true,
    sites: [siteId],
    site_id: siteId,
    ...extraParams,
  }
}

export const COLLECTION_FILTER_ENTITIES = {
  impression: [
    'allocation',
    'path',
    'page',
    'pathUserProfile',
    'publisher',
    'offer',
  ],
  session: [
    'publisher',
    'allocation',
    'form',
    'lander',
    'postVersion',
  ],
  bucketrans: [
    'organization',
    'site',
    'allocation',
    'bucket',
    'publisher',
  ],
  integration: [
    'publisher',
    'integration',
    'allocation',
  ],
}

export const ENTITY_LABELS = {
  [CUSTOM_FILTER_ENTITY]: 'Custom',
  allocation: 'Allocation',
  path: 'Path',
  page: 'Page',
  pathUserProfile: 'Path User Profile',
  publisher: 'Publisher',
  offer: 'Offer',
  form: 'Form',
  lander: 'Lander',
  postVersion: 'Post Version',
  organization: 'Organization',
  site: 'Site',
  bucket: 'Bucket',
  integration: 'Integration',
}

export const ENTITY_LIST_CONFIG = {
  allocation: { listKey: 'allocations', valueKey: 'id', labelKey: 'description' },
  path: { listKey: 'paths', valueKey: 'id', labelKey: 'name' },
  page: { listKey: 'pages', valueKey: 'id', labelKey: 'name' },
  publisher: { listKey: 'publishers', valueKey: 'id', labelKey: 'name' },
  offer: { listKey: 'offers', valueKey: 'id', labelKey: 'name' },
  integration: { listKey: 'integrations', valueKey: 'id', labelKey: 'description' },
  form: { listKey: 'forms', valueKey: 'id', labelKey: 'name' },
  organization: { listKey: 'organizations', valueKey: 'id', labelKey: 'name' },
  site: { listKey: 'sites', valueKey: 'id', labelKey: 'name' },
  pathUserProfile: { listKey: 'userProfiles', valueKey: 'id', labelKey: 'name' },
  lander: { listKey: 'landers', valueKey: 'id', labelKey: 'title' },
  postVersion: { listKey: 'postVersions', valueKey: 'value', labelKey: 'label' },
  bucket: { listKey: 'buckets', valueKey: 'value', labelKey: 'label' },
}

export function getEntityLabel(entity) {
  return ENTITY_LABELS[entity] || entity
}

export function getCollectionFilterEntities(collection) {
  return [CUSTOM_FILTER_ENTITY, ...COLLECTION_FILTER_ENTITIES[collection] || []]
}

const createFilterId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

export function createFilterItem(entity = null, values = []) {
  return {
    id: createFilterId(),
    entity,
    values: values || [],
  }
}

export function filterbyToFilters(filterby = {}) {
  return Object.entries(filterby).map(([entity, values]) => (
    createFilterItem(entity, values)
  ))
}

export function conditionsToFilters(conditions = []) {
  return conditions.map(condition => ({
    ...createFilterItem(CUSTOM_FILTER_ENTITY),
    ...conditionToCustomFilter(condition),
  }))
}

export function filtersToConditions(filters = []) {
  return filters
    .filter(filter => filter?.entity === CUSTOM_FILTER_ENTITY)
    .map(customFilterToCondition)
    .filter(Boolean)
}

export function filtersToFilterby(filters = []) {
  return filters.reduce((acc, filter) => {
    if (!filter?.entity || filter.entity === CUSTOM_FILTER_ENTITY) return acc
    if (!filter.values?.length) return acc

    const merged = [...(acc[filter.entity] || []), ...filter.values]
    acc[filter.entity] = [...new Set(merged.map(value => String(value)))].map(value => {
      const numeric = Number(value)
      return Number.isNaN(numeric) ? value : numeric
    })

    return acc
  }, {})
}

export function pruneFiltersForCollection(filters = [], collection) {
  const allowed = new Set(getCollectionFilterEntities(collection))
  const allowedFields = new Set(getCollectionFields(collection).map(field => field.value))

  return filters.map(filter => {
    if (!filter.entity) return filter

    if (!allowed.has(filter.entity)) {
      return { ...filter, entity: null, values: [] }
    }

    if (filter.entity === CUSTOM_FILTER_ENTITY && filter.field && !allowedFields.has(filter.field)) {
      return { ...filter, field: null, value: '' }
    }

    return filter
  })
}

export function getAvailableFilterEntities(collection, activeEntities = []) {
  return getCollectionFilterEntities(collection).filter(
    entity => entity === CUSTOM_FILTER_ENTITY || !activeEntities.includes(entity),
  )
}

export function pruneFilterbyForCollection(filterby = {}, collection) {
  return filtersToFilterby(
    pruneFiltersForCollection(filterbyToFilters(filterby), collection),
  )
}

export function normalizeFilterby(filterby = {}) {
  return Object.entries(filterby).reduce((acc, [key, values]) => {
    if (!values?.length) return acc

    acc[key] = values.map(value => {
      const numeric = Number(value)
      return Number.isNaN(numeric) ? value : numeric
    })

    return acc
  }, {})
}

export function formatEntityOptionLabel(item, config) {
  const value = item[config.valueKey]
  const label = item[config.labelKey] ?? value

  if (config.valueKey === 'value' && item.label) return item.label
  if (typeof label === 'string' && label.startsWith('[#')) return label

  return `[#${value}] - ${label}`
}

export function mapEntityItems(items = [], config) {
  return items
    .filter(item => item?.[config.valueKey] != null)
    .map(item => ({
      value: item[config.valueKey],
      label: formatEntityOptionLabel(item, config),
    }))
}
